import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    const path =
      window.location.pathname.toLowerCase();

    const canonical =
      (
        document.querySelector(
          'link[rel="canonical"]'
        )?.href || ''
      ).toLowerCase();

    // ==================================================
    // ANIMALIZA CUSTOM PRODUCTS
    // ==================================================

    const isAnimalizaCustomProduct =

      // ==================================================
      // CAP
      // ==================================================

      (
        path.includes('/products/') &&
        path.includes('pet-cap')
      ) ||
      (
        canonical.includes('/products/') &&
        canonical.includes('pet-cap')
      ) ||

      // ==================================================
      // HOODIE BORDADA
      // ==================================================

      path.includes(
        '/products/custom-embroidered-pet-hoodie'
      ) ||
      canonical.includes(
        '/products/custom-embroidered-pet-hoodie'
      ) ||

      // ==================================================
      // HOODIE DTG / PRINTED
      // ==================================================

      path.includes(
        '/products/custom-printed-pet-hoodie'
      ) ||
      canonical.includes(
        '/products/custom-printed-pet-hoodie'
      ) ||

      // ==================================================
      // T-SHIRT BORDADA
      // ==================================================

      path.includes(
        '/products/custom-embroidered-pet-t-shirt'
      ) ||
      canonical.includes(
        '/products/custom-embroidered-pet-t-shirt'
      ) ||

      // ==================================================
      // T-SHIRT DTG / PRINTED
      // ==================================================

      path.includes(
        '/products/custom-printed-pet-t-shirt'
      ) ||
      canonical.includes(
        '/products/custom-printed-pet-t-shirt'
      ) ||

      // ==================================================
      // SWEATSHIRT BORDADA
      // ==================================================

      path.includes(
        '/products/custom-embroidered-pet-sweatshirt'
      ) ||
      canonical.includes(
        '/products/custom-embroidered-pet-sweatshirt'
      ) ||

      // ==================================================
      // SWEATSHIRT DTG / PRINTED
      // ==================================================

      path.includes(
        '/products/custom-printed-pet-sweatshirt'
      ) ||
      canonical.includes(
        '/products/custom-printed-pet-sweatshirt'
      ) ||

      path.includes(
        '/products/printed-pet-sweatshirt'
      ) ||
      canonical.includes(
        '/products/printed-pet-sweatshirt'
      ) ||

      path.includes(
        '/products/dtg-sweatshirt'
      ) ||
      canonical.includes(
        '/products/dtg-sweatshirt'
      ) ||

      path.includes(
        '/products/pet-sweatshirt-dtg'
      ) ||
      canonical.includes(
        '/products/pet-sweatshirt-dtg'
      ) ||

      (
        path.includes('/products/') &&
        (
          path.includes('pet-sweatshirt') ||
          path.includes('unisex-premium-sweatshirt')
        )
      ) ||
      (
        canonical.includes('/products/') &&
        (
          canonical.includes('pet-sweatshirt') ||
          canonical.includes('unisex-premium-sweatshirt')
        )
      ) ||

      // ==================================================
      // MUG
      // ==================================================

      (
        path.includes('/products/') &&
        (
          path.includes('pet-portrait-mug') ||
          path.includes('pet-mug')
        )
      ) ||
      (
        canonical.includes('/products/') &&
        (
          canonical.includes('pet-portrait-mug') ||
          canonical.includes('pet-mug')
        )
      ) ||

      // ==================================================
      // TOTE BAG
      // ==================================================

      (
        path.includes('/products/') &&
        path.includes('tote-bag')
      ) ||
      (
        canonical.includes('/products/') &&
        canonical.includes('tote-bag')
      );

    if (isAnimalizaCustomProduct) {
      return;
    }

    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const newMediaGallery =
          html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      {
        animate: false,
      }
    );
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(
      index,
      event
    );
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    MediaGallery
  );
}